import PropTypes from "prop-types";
import { CheckOutlined } from "@/config/antd";
import "@/assets/styles/custom_steps.css";

const Steps = ({
  current,
  steps,
  className = "",
  allComplete = false,
  mode = "multi-page",
  sectionsCompleted = [],
}) => {
  // Calculate the flex values for dash lines based on active step
  const getDashFlexValue = (index) => {
    // For the last step, there's no dash
    if (index === steps.length - 1) return 0;

      if (index < current) {
      // Dashes before current step should be very small to collapse previous steps
      return 0.5;
    } else if (index === current) {
      // Dash after current step should be much larger to give space for title
      return 6;
    } else {
      // Dashes for future steps should be very collapsed
      return 0.5;
    }
  };

  // Determine step state: active, next, inactive, or completed
  const getStepState = (index) => {
    if (allComplete) {
      return "completed";
    } else if (sectionsCompleted.includes(index)) {
      return "completed";
    } else if (index === current) {
      return "active";
    } else if (index === current + 1) {
      return "next";
    } else {
      return "inactive";
    }
  };

  return (
    <div className={`custom-steps-container ${className}`}>
      {/* Mobile view - horizontal layout showing all step circles but only active step title */}
      <div className="lg:hidden">
        <div className="custom-steps-mobile">
          {steps.map((step, index) => {
            const stepState = getStepState(index);
            return (
              <div
                key={index}
                className={`custom-step-mobile-wrapper ${
                  index === current
                    ? "active-step-wrapper"
                    : "inactive-step-wrapper"
                }`}>
                <div className={`custom-step-mobile ${stepState}`}>
                  <div
                    className={`custom-step-icon ${
                      index !== current ? "collapsed-step" : ""
                    }`}>
                    {stepState === "completed" ? (
                      <CheckOutlined className="custom-step-check" />
                    ) : (
                      <span className="custom-step-number">{index + 1}</span>
                    )}
                  </div>

                  {/* Only show title for active step */}
                  {index === current && (
                    <div className="custom-step-content">
                      <div className="custom-step-title">{step.title}</div>
                    </div>
                  )}
                </div>

                {/* Dash line between steps with dynamic flex value */}
                {index < steps.length - 1 && (
                  <div
                    className={`custom-step-dash ${
                      sectionsCompleted.includes(index)
                        ? "completed-dash"
                        : "pending-dash"
                    }`}
                    style={{ flex: getDashFlexValue(index) }}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop view - different layouts for multi-page vs single-page */}
      <div className="hidden lg:block">
        {mode === "multi-page" ? (
          // Standard vertical steps for multi-page flows
          steps.map((step, index) => {
            const stepState = getStepState(index);
            return (
              <div key={index} className={`custom-step-vertical ${stepState}`}>
                <div className="custom-step-vertical-content">
                  <div className="custom-step-icon">
                    {stepState === "completed" ? (
                      <CheckOutlined className="custom-step-check" />
                    ) : (
                      <span className="custom-step-number">{index + 1}</span>
                    )}
                  </div>
                  <div className="custom-step-content">
                    <div className="custom-step-title">{step.title}</div>
                  </div>
                </div>

                {/* Dash line between steps */}
                {index < steps.length - 1 && (
                  <div
                    className={`custom-step-vertical-dash ${
                      sectionsCompleted.includes(index)
                        ? "completed-dash"
                        : "pending-dash"
                    }`}></div>
                )}
              </div>
            );
          })
        ) : (
          // Section-based steps for single-page forms
          <div className="single-page-steps">
            {steps.map((step, index) => {
              const stepState = getStepState(index);
              return (
                <div
                  key={index}
                  className={`custom-step-vertical ${stepState}`}>
                  <div className="custom-step-vertical-content">
                    <div className="custom-step-icon">
                      {stepState === "completed" ? (
                        <CheckOutlined className="custom-step-check" />
                      ) : (
                        <span className="custom-step-number">{index + 1}</span>
                      )}
                    </div>
                    <div className="custom-step-content">
                      <div className="custom-step-title">{step.title}</div>
                    </div>
                  </div>

                  {/* Dash line between steps - shorter for single-page mode */}
                  {index < steps.length - 1 && (
                    <div
                      className={`custom-step-vertical-dash ${
                        sectionsCompleted.includes(index)
                          ? "completed-dash"
                          : "pending-dash"
                      }`}></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

Steps.propTypes = {
  current: PropTypes.number,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  allComplete: PropTypes.bool,
  mode: PropTypes.oneOf(["multi-page", "single-page"]),
  sectionsCompleted: PropTypes.arrayOf(PropTypes.number),
};

export default Steps;
